import RoomOccupancy from './RoomOccupancy'
import Image from './Image'
import RoomService from './RoomService'
import { buildImageUrl, buildImageUrlFromMedia } from '@/infrastructure/Utils'

export default class Room {
  static fromJSON (json) {
    return new Room({
      id: json.id,
      name: json.name,
      tmsName: json.tmsName,
      description: json.description,
      defaultImage: Image.fromJSON(json.image),
      occupancy: RoomOccupancy.fromJSON(json.occupancy),
      bedType: json.bedType,
      size: json.size,
      services: (json.services || []).map(RoomService.fromJSON),
      groups: json.groups,
      jsonForIntegrateWithGallery: json
    })
  }

  constructor ({ id, name, tmsName, description, defaultImage, occupancy, bedType, size, services, groups, jsonForIntegrateWithGallery }) {
    this.id = id
    this.name = name
    this.tmsName = tmsName
    this.description = description
    this.defaultImage = defaultImage
    this.occupancy = occupancy || {}
    this.bedType = bedType
    this.size = size
    this.services = services || []
    this.groups = groups || []
    this.maxDiscount = this.takeAllStays().reduce((acc, stay) => stay.percentageOfDiscountPerNight > acc ? stay.percentageOfDiscountPerNight : acc, 0)
    this.isPayUponArrival = this.takeAllStays().filter(stay => stay.isPayUponArrival()).length > 0
    this.jsonForIntegrateWithGallery = jsonForIntegrateWithGallery
    this.numberOfRooms = Math.min(...this.takeAllStays().map(stay => stay.numberOfRoomsLeft))
    this.galleryImages = this.getGalleryImages()
  }

  hasAvailableStays () {
    return this.takeAllStays().length > 0
  }

  getCheaperStay () {
    return this.takeAllStays().reduce((acc, curr) =>
      acc.orderBy.cashAndPoints > curr.orderBy.cashAndPoints ? curr : acc, this.takeAllStays()[0])
  }

  takeAllStays () {
    return this.groups.reduce((acc, curr) => [...acc, ...curr.getAllStays()], [])
  }

  hasTaxIncluded() {
    return this.takeAllStays().every(stay => stay.includesTaxes === true)
  }

  count (filter) {
    return this.takeAllStays().reduce((accumulation, stay) => {
      const matches = stay.count(filter)
      Object.keys(matches)
        .forEach((key) => {
          accumulation[key] = (accumulation[key] || 0) + matches[key]
        })
      return accumulation
    }, {})
  }

  applyFilter (filter, isLogged) {
    const groups = this.groups.map(group => group.applyFilter(filter))

    const sortedGroups = groups.sort((a, b) => a.orderBy.priorityAndCashAndPoints - b.orderBy.priorityAndCashAndPoints)

    return new Room({
      ...this,
      groups: sortedGroups
    })
  }

  isAccessible () {
    return this.takeAllStays().filter(stay => stay.accessible).length > 0
  }

  changeCurrency (currency) {
    return new Room({
      ...this,
      groups: this.groups.map(group => group.changeCurrency(currency))
    })
  }

  hasGallery () {
    return this.jsonForIntegrateWithGallery && this.jsonForIntegrateWithGallery.gallery && this.jsonForIntegrateWithGallery.gallery.length > 0
  }

  getGalleryImages () {
    if (!this.hasGallery()) {
      return [
        {
          alt: this.jsonForIntegrateWithGallery.image.altText,
          src: buildImageUrlFromMedia(this.jsonForIntegrateWithGallery.image, 'GalleryLightbox'),
          srcThumbnail: buildImageUrlFromMedia(this.jsonForIntegrateWithGallery.image, 'GalleryLightboxThum')
        }
      ]
    }
    return this.jsonForIntegrateWithGallery.gallery.map((slide, index) => {
      let item
      if (slide.mimeType && slide.mimeType.includes('video')) {
        item = {
          id: this.id + index,
          name: slide.name,
          posterUrl: buildImageUrl(slide.posterUrl, 'GalleryLightbox', slide.gravity),
          thumbnailUrl: buildImageUrl(slide.thumbnailUrl, 'GalleryLightboxThum', slide.gravity),
          videoUrl: slide.url,
          title: slide.title,
          languagesInfo: slide.captions
        }
      } else {
        item = {
          alt: slide.altText,
          src: buildImageUrlFromMedia(slide, 'GalleryLightbox'),
          srcThumbnail: buildImageUrlFromMedia(slide, 'GalleryLightboxThum')
        }
      }
      return item
    })
  }

  getOnlyCashStays () {
    if (!this.takeAllStays()) return []
    return this.takeAllStays().filter(stay => !stay.points.hasPoints() && !!stay.cash.hasCash())
  }

  getOnlyPointsStays (customerPoints) {
    if (!this.takeAllStays()) return []
    return this.takeAllStays().filter(stay => !!stay.points.hasPoints() && !stay.cash.hasCash() && stay.points.amountPerNight <= customerPoints)
  }

  getCashAndPointsStays (customerPoints) {
    if (!this.takeAllStays()) return []
    return this.takeAllStays().filter(stay => !!stay.points.hasPoints() && !!stay.cash.hasCash() && stay.points.amountPerNight <= customerPoints)
  }
}
